if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.setupCustomLengthValidation();
      }

      setupCustomLengthValidation() {
        // Custom Length input is rendered as a sibling element, not inside the form
        // It is associated with the form via the form="..." attribute
        // We must search the document scope for elements with matching form attribute
        const formId = this.form.id;
        let customLengthInput = this.form.querySelector('[data-length-label]');

        if (!customLengthInput && formId) {
          // Search by form attribute - finds elements associated with this form
          customLengthInput = document.querySelector('[data-length-label][form="' + formId + '"]');
        }

        if (!customLengthInput) {
          // Fallback: search in closest section (same scope where blocks render)
          const parentSection = this.closest('section') || this.closest('[id^="ProductInfo-"]');
          if (parentSection) {
            customLengthInput = parentSection.querySelector('[data-length-label]');
          }
        }

        if (!customLengthInput) return;

        this.customLengthInput = customLengthInput;

        customLengthInput.addEventListener('input', () => {
          this.validateCustomLengthAndUpdateUI();
        });

        customLengthInput.addEventListener('blur', () => {
          this.validateCustomLengthAndUpdateUI();
        });

        customLengthInput.addEventListener('change', () => {
          this.validateCustomLengthAndUpdateUI();
        });

        this.validateCustomLengthAndUpdateUI();
      }

      validateCustomLength() {
        if (!this.customLengthInput) return { valid: true };

        const container = this.customLengthInput.closest('.product-form__custom-length');
        if (!container) return { valid: true };

        const hint = container.querySelector('.custom-length-hint');
        const errorEl = container.querySelector('.custom-length-error');
        const minLength = parseFloat(this.customLengthInput.dataset.minLength);
        const maxLength = parseFloat(this.customLengthInput.dataset.maxLength);
        const lengthLabel = this.customLengthInput.dataset.lengthLabel;
        const value = this.customLengthInput.value.trim();
        const numVal = parseFloat(value);

        if (value === '' || isNaN(numVal)) {
          if (errorEl) {
            errorEl.textContent = lengthLabel + ' is required. Enter a length between ' + minLength + ' and ' + maxLength + ' inches.';
            errorEl.style.display = 'block';
          }
          if (hint) hint.style.display = 'none';
          this.customLengthInput.classList.add('is-error');
          return { valid: false, input: this.customLengthInput };
        }

        if (numVal < minLength || numVal > maxLength) {
          if (errorEl) {
            errorEl.textContent = 'Length must be between ' + minLength + ' and ' + maxLength + ' inches.';
            errorEl.style.display = 'block';
          }
          if (hint) hint.style.display = 'none';
          this.customLengthInput.classList.add('is-error');
          return { valid: false, input: this.customLengthInput };
        }

        if (errorEl) errorEl.style.display = 'none';
        if (hint) hint.style.display = 'block';
        this.customLengthInput.classList.remove('is-error');
        return { valid: true };
      }

      validateCustomLengthAndUpdateUI() {
        const result = this.validateCustomLength();
        this.updateButtonStates(result.valid);
        return result.valid;
      }

      updateButtonStates(valid) {
        if (this.submitButton) {
          this.submitButton.disabled = !valid;
        }

        const dynamicBtns = this.querySelectorAll('.shopify-payment-button__button, [data-shopify-payment-button]');
        dynamicBtns.forEach((btn) => {
          if (valid) {
            btn.disabled = false;
            btn.style.pointerEvents = '';
            btn.style.opacity = '';
          } else {
            btn.disabled = true;
            btn.style.pointerEvents = 'none';
            btn.style.opacity = '0.6';
          }
        });
      }

      handleCustomLengthError(input) {
        if (input) {
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            if (input) input.focus();
          }, 300);
        }
      }

      onSubmitHandler(evt) {
        const customLengthResult = this.validateCustomLength();
        if (!customLengthResult.valid) {
          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation();
          this.handleCustomLengthError(customLengthResult.input);
          this.updateButtonStates(false);
          return;
        }

        this.updateButtonStates(true);
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
